import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Eye, Check, X, Pin, PinOff, ArrowUp, ArrowDown, CalendarClock } from "lucide-react";
import { http, fileUrl, fmtViews } from "../lib/api";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const fmtWhen = (iso) => new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

const Row = ({ item, onChange }) => {
  const [edit, setEdit] = useState(false);
  const scheduled = item.publish_at && new Date(item.publish_at) > new Date();
  const [f, setF] = useState({ title: item.title, category: item.category, tags: item.tags.join(", "), featured: item.featured, when: scheduled ? toLocalInput(item.publish_at) : "" });

  const save = async () => {
    const { when, ...rest } = f;
    if (when && new Date(when) <= new Date()) return toast.error("A data agendada precisa estar no futuro");
    try {
      await http.patch(`/admin/media/${item.id}`, { ...rest, publish_at: when ? new Date(when).toISOString() : "" });
      toast.success(when ? `Agendado para ${fmtWhen(new Date(when).toISOString())}` : "Atualizado");
      setEdit(false);
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao salvar");
    }
  };
  const publishNow = async () => {
    await http.patch(`/admin/media/${item.id}`, { publish_at: "" }).then(() => { toast.success("Publicado agora"); onChange(); }).catch(() => toast.error("Falha ao publicar"));
  };
  const del = async () => {
    if (!window.confirm(`Excluir "${item.title}"?`)) return;
    await http.delete(`/admin/media/${item.id}`).catch(() => toast.error("Falha ao excluir"));
    toast.success("Excluído");
    onChange();
  };
  const inp = "w-full border border-slate-800 bg-black px-2 py-1 text-sm text-white outline-none focus:border-rose-500";
  const pin = async () => {
    await http.patch(`/admin/media/${item.id}`, { pinned: !item.pinned })
      .then(() => { toast.success(item.pinned ? "Desafixado" : "Fixado no topo de Vídeos"); onChange(); })
      .catch(() => toast.error("Falha ao fixar"));
  };
  const move = async (direction) => {
    await http.post(`/admin/media/${item.id}/pin-move`, null, { params: { direction } }).then(onChange).catch(() => toast.error("Falha ao mover"));
  };
  const btn = "p-2 hover:bg-white/5";

  return (
    <div className="grid grid-cols-[72px_1fr] gap-4 border-b border-white/5 py-4 sm:grid-cols-[96px_1fr_auto]" data-testid={`admin-row-${item.id}`}>
      <div className="relative aspect-video overflow-hidden bg-white/5">
        {(item.has_thumb || item.type === "photo") && <img src={fileUrl(item.id, item.has_thumb ? "thumb" : "file")} alt="" className="h-full w-full object-cover" />}
      </div>
      {edit ? (
        <div className="grid gap-2 sm:grid-cols-3">
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} data-testid="edit-title-input" />
          <input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Categoria" className={inp} data-testid="edit-category-input" />
          <input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} placeholder="Tags" className={inp} data-testid="edit-tags-input" />
          <label className="flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} className="accent-rose-600" /> Destaque</label>
          <label className="flex items-center gap-2 text-xs text-slate-300 sm:col-span-2">
            <CalendarClock size={14} className="shrink-0 text-rose-400" /> Publicar em
            <input type="datetime-local" value={f.when} onChange={(e) => setF({ ...f, when: e.target.value })} className={`${inp} [color-scheme:dark]`} data-testid="edit-schedule-input" />
            {f.when && <button type="button" onClick={() => setF({ ...f, when: "" })} className="shrink-0 text-slate-400 hover:text-white" data-testid="edit-schedule-clear">limpar</button>}
          </label>
        </div>
      ) : (
        <div className="min-w-0">
          <p className="truncate text-white">{item.title}</p>
          <p className="font-mono-d mt-1 flex flex-wrap gap-3 text-xs text-slate-400">
            <span className="uppercase text-rose-400">{item.type === "video" ? "Vídeo" : "Foto"}</span>
            {item.pinned && <span className="flex items-center gap-1 text-amber-300" data-testid={`pinned-badge-${item.id}`}><Pin size={12} /> Fixado</span>}
            <span>{item.category || "—"}</span>
            <span className="flex items-center gap-1"><Eye size={12} /> {fmtViews(item.views)}</span>
            {scheduled ? (
              <span className="flex items-center gap-1 text-sky-300" data-testid={`scheduled-badge-${item.id}`}><CalendarClock size={12} /> Agendado: {fmtWhen(item.publish_at)}</span>
            ) : (
              <span>{new Date(item.created_at).toLocaleDateString("pt-BR")}</span>
            )}
          </p>
        </div>
      )}
      <div className="col-span-2 flex gap-2 sm:col-span-1">
        {edit ? (
          <>
            <button onClick={save} className="p-2 text-emerald-400 hover:bg-white/5" data-testid={`save-${item.id}`} aria-label="Salvar"><Check size={16} /></button>
            <button onClick={() => setEdit(false)} className="p-2 text-slate-400 hover:bg-white/5" data-testid={`cancel-${item.id}`} aria-label="Cancelar"><X size={16} /></button>
          </>
        ) : (
          <>
            {scheduled && (
              <button onClick={publishNow} className="px-2 text-xs uppercase tracking-widest text-sky-300 hover:bg-white/5" data-testid={`publish-now-${item.id}`}>Publicar agora</button>
            )}
            {item.type === "video" && (
              <button onClick={pin} className={`${btn} ${item.pinned ? "text-amber-300" : "text-slate-300"}`} data-testid={`pin-${item.id}`} aria-label={item.pinned ? "Desafixar" : "Fixar no topo"} title={item.pinned ? "Desafixar" : "Fixar no topo de Vídeos"}>
                {item.pinned ? <PinOff size={16} /> : <Pin size={16} />}
              </button>
            )}
            {item.pinned && (
              <>
                <button onClick={() => move("up")} className={`${btn} text-slate-300`} data-testid={`pin-up-${item.id}`} aria-label="Subir"><ArrowUp size={16} /></button>
                <button onClick={() => move("down")} className={`${btn} text-slate-300`} data-testid={`pin-down-${item.id}`} aria-label="Descer"><ArrowDown size={16} /></button>
              </>
            )}
            <button onClick={() => setEdit(true)} className="p-2 text-slate-300 hover:bg-white/5 hover:text-white" data-testid={`edit-${item.id}`} aria-label="Editar"><Pencil size={16} /></button>
            <button onClick={del} className="p-2 text-rose-400 hover:bg-rose-950" data-testid={`delete-${item.id}`} aria-label="Excluir"><Trash2 size={16} /></button>
          </>
        )}
      </div>
    </div>
  );
};

export const ManageList = ({ items, onChange }) =>
  items.length === 0 ? (
    <p className="py-10 text-slate-400" data-testid="manage-empty">Nenhum conteúdo publicado ainda.</p>
  ) : (
    <div data-testid="manage-list">{items.map((i) => <Row key={i.id} item={i} onChange={onChange} />)}</div>
  );
